import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  useColorModeValue,
  Container,
  Code,
  IconButton,
  useClipboard,
  Tooltip,
  Flex,
  Divider,
  Button,
  HStack,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Progress
} from '@chakra-ui/react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { FiCopy, FiPlus, FiMessageSquare, FiSettings, FiActivity, FiMic, FiEdit2, FiTrash2, FiUserCheck, FiUsers, FiShield } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const WidgetPage = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  // State for embed codes (can be common)
  const [receivedEmbedCodes, setReceivedEmbedCodes] = useState({});
  const [selectedWidgetType, setSelectedWidgetType] = useState('all');



  // User-specific widget data


  useEffect(() => {
    // Simulate fetching embed codes based on user role
    const dummyCodes = isAdmin
      ? { // Admin might have different embeddable widgets or none
        chatbot: '<iframe src="YOUR_EMBED_URL/embed/chatbot/user/3" width="400" height="200" frameborder="0"></iframe>',
        voice_agent: '<iframe src="YOUR_EMBED_URL/embed/voice-agent/user/3" width="400" height="200" frameborder="0"></iframe>',
        }
      : { // User has chatbot and voice agent widgets
          chatbot: '<iframe src="YOUR_EMBED_URL/embed/chatbot/user/3" width="400" height="200" frameborder="0"></iframe>',
          voice_agent: '<iframe src="YOUR_EMBED_URL/embed/voice-agent/user/3" width="400" height="200" frameborder="0"></iframe>',
        };
    setReceivedEmbedCodes(dummyCodes);
  }, [isAdmin]);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'whiteAlpha.900');

  // Helper component for embed code section (remains the same)
  const EmbedCodeSection = ({ widgetType, code }) => {
    const { hasCopied, onCopy } = useClipboard(code || '');
    const headingText = widgetType === 'chatbot' ? 'Chatbot Widget Code' : 'Voice Agent Widget Code';

    if (!code) return null;

    return (
      <VStack align="stretch" spacing={4} mt={4}>
        <Heading size="md">Your {headingText}</Heading>
        <Text color={textColor}>Copy the code below to embed your {headingText} on your website or platform.</Text>
        <Flex align="center">
          <Code flex="1" p={4} overflowX="auto" whiteSpace="pre" mr={4}>
            {code}
          </Code>
          <Tooltip label={hasCopied ? "Copied!" : "Copy code"} closeOnClick={false}>
            <IconButton
              icon={<FiCopy />}
              aria-label={`Copy ${headingText} code`}
              onClick={onCopy}
            />
          </Tooltip>
        </Flex>
      </VStack>
    );
  };

  // Admin Widgets Layout

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <Box
        ml={{ base: 0, md: "64" }}
        transition="margin-left 0.2s"
        minH="100vh"
        position="relative"
      >
        <Navbar />
        <Box py={8} px={{ base: 4, md: 8 }}>
          <VStack spacing={6} align="stretch">
            <Heading size="xl" mb={4}>{isAdmin ? "Admin Widgets" : "My Widgets"}</Heading>
            <Text fontSize="lg" color={textColor}>View and manage your assigned widgets and statistics.</Text>

            <Tabs variant="enclosed" colorScheme="blue">
              <TabList>
                <Tab>Widgets</Tab>
                {/* Add more tabs if needed, e.g., Admin specific tabs */}
              </TabList>

              <TabPanels>
                {/* Widgets Tab Panel (Common for both, but might show different embed codes) */}
                <TabPanel>
                   <VStack spacing={6} align="stretch">
                     <Heading size="lg">Available Widgets</Heading>
                     <Text color={textColor}>View and copy the embed codes for your assigned widgets.</Text>

                     <HStack spacing={4} mb={4}>
                       <Button
                         size="sm"
                         onClick={() => setSelectedWidgetType('all')}
                         colorScheme={selectedWidgetType === 'all' ? 'blue' : 'gray'}
                         variant={selectedWidgetType === 'all' ? 'solid' : 'outline'}
                       >
                         All
                       </Button>
                       <Button
                         size="sm"
                         onClick={() => setSelectedWidgetType('chatbot')}
                         colorScheme={selectedWidgetType === 'chatbot' ? 'blue' : 'gray'}
                         variant={selectedWidgetType === 'chatbot' ? 'solid' : 'outline'}
                         isDisabled={!receivedEmbedCodes.chatbot}
                       >
                         Chatbot
                       </Button>
                       <Button
                         size="sm"
                         onClick={() => setSelectedWidgetType('voice_agent')}
                         colorScheme={selectedWidgetType === 'voice_agent' ? 'blue' : 'gray'}
                         variant={selectedWidgetType === 'voice_agent' ? 'solid' : 'outline'}
                         isDisabled={!receivedEmbedCodes.voice_agent}
                       >
                         Voice Agent
                       </Button>
                     </HStack>

                     <Card bg={cardBg} boxShadow="md" borderRadius="lg">
                       <CardBody p={6}>
                         {(selectedWidgetType === 'all' || selectedWidgetType === 'chatbot') && receivedEmbedCodes.chatbot && (
                           <EmbedCodeSection widgetType="chatbot" code={receivedEmbedCodes.chatbot} />
                         )}
                         {(selectedWidgetType === 'all' || selectedWidgetType === 'voice_agent') && receivedEmbedCodes.voice_agent && (
                           <>
                             {selectedWidgetType === 'all' && receivedEmbedCodes.chatbot && <Divider mt={6} />}
                             <EmbedCodeSection widgetType="voice_agent" code={receivedEmbedCodes.voice_agent} />
                           </>
                         )}

                         {(!receivedEmbedCodes.chatbot && !receivedEmbedCodes.voice_agent) ||
                          (selectedWidgetType === 'chatbot' && !receivedEmbedCodes.chatbot) ||
                          (selectedWidgetType === 'voice_agent' && !receivedEmbedCodes.voice_agent) ? (
                           <Text color={textColor}>No widget embed code found for the selected type.</Text>
                         ) : null}
                       </CardBody>
                     </Card>
                   </VStack>
                 </TabPanel>

                {/* Stats Tab Panel (Role-Specific) */}
             
              </TabPanels>
            </Tabs>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default WidgetPage; 